// src/pages/MyBookings.js

import React, { useState, useEffect } from 'react';
import {
    Container, Typography, Box, CircularProgress, IconButton
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { Link as RouterLink } from 'react-router-dom';
import { getUserTransactions } from '../services/api';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import FilterListIcon from '@mui/icons-material/FilterList';
import { useAuth } from '../context/AuthContext';
import BookingList from '../components/BookingList';
import FilterModal from '../components/FilterModal';

const StyledContainer = styled(Container)(({ theme }) => ({
    marginTop: theme.spacing(4),
    marginBottom: theme.spacing(4),
    maxWidth: 'md',
}));

function MyBookings() {
    const { currentUser, loading: authLoading } = useAuth();
    const [bookings, setBookings] = useState([]);
    const [sortBy, setSortBy] = useState('rentalPeriodStart');
    const [tempSortBy, setTempSortBy] = useState('rentalPeriodStart');
    const [filterModalOpen, setFilterModalOpen] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [tempSearchTerm, setTempSearchTerm] = useState('');
    const [loading, setLoading] = useState(true); // Local loading state for bookings

    useEffect(() => {
        if (!authLoading && currentUser) {
            const fetchBookings = async () => {
                try {
                    const response = await getUserTransactions();
                    // Filter bookings where current user is the borrower and the status is current (active)
                    const borrowerBookings = response.data.filter(
                        (booking) =>
                            booking.borrower_id === currentUser.id &&
                            ['pending', 'accepted', 'paid', 'in_progress'].includes(booking.status)
                    );
                    setBookings(borrowerBookings || []);
                } catch (error) {
                    console.error('Error fetching bookings:', error);
                } finally {
                    setLoading(false);
                }
            };

            fetchBookings();
        }
    }, [authLoading, currentUser]);

    const handleOpenFilterModal = () => {
        setTempSortBy(sortBy);
        setTempSearchTerm(searchTerm);
        setFilterModalOpen(true);
    };

    const handleCloseFilterModal = () => {
        setFilterModalOpen(false);
    };

    const handleUpdateFilter = () => {
        setSortBy(tempSortBy);
        setSearchTerm(tempSearchTerm);
        setFilterModalOpen(false);
    };

    const handleCancelFilter = () => {
        setFilterModalOpen(false);
    };

    const sortedAndFilteredBookings = bookings
        .filter(
            (booking) =>
                (booking.listing_title || '')
                    .toLowerCase()
                    .includes(searchTerm.toLowerCase()) ||
                (booking.listing_owner_name || '')
                    .toLowerCase()
                    .includes(searchTerm.toLowerCase())
        )
        .sort((a, b) => {
            if (sortBy === 'lastActivity') {
                return new Date(b.updated_at || 0) - new Date(a.updated_at || 0);
            } else if (sortBy === 'rentalPeriodStart') {
                return new Date(a.start_date || 0) - new Date(b.start_date || 0);
            } else if (sortBy === 'rentalPeriodEnd') {
                return new Date(a.end_date || 0) - new Date(b.end_date || 0);
            }
            return 0;
        });

    if (authLoading || loading) {
        return (
            <StyledContainer>
                <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
                    <CircularProgress />
                </Box>
            </StyledContainer>
        );
    }

    if (!currentUser) {
        return (
            <StyledContainer>
                <Typography variant="h4" gutterBottom>My Bookings</Typography>
                <Typography variant="body1">
                    You need to be logged in to view your bookings.
                </Typography>
            </StyledContainer>
        );
    }

    return (
        <StyledContainer>
            <Box display="flex" alignItems="center" mb={2}>
                <IconButton component={RouterLink} to="/bookings" edge="start" sx={{ mr: 2 }}>
                    <ArrowBackIcon />
                </IconButton>
                <Typography variant="h4" component="h1" flexGrow={1}>
                    My Current Bookings
                </Typography>
                <IconButton onClick={handleOpenFilterModal}>
                    <FilterListIcon />
                </IconButton>
            </Box>

            <BookingList bookings={sortedAndFilteredBookings} role="borrower" />

            {sortedAndFilteredBookings.length === 0 && (
                <Typography>No current bookings found.</Typography>
            )}

            <FilterModal
                open={filterModalOpen}
                onClose={handleCloseFilterModal}
                title="Filter My Bookings"
                searchTerm={searchTerm}
                setSearchTerm={setSearchTerm}
                tempSearchTerm={tempSearchTerm}
                setTempSearchTerm={setTempSearchTerm}
                sortBy={sortBy}
                setSortBy={setSortBy}
                tempSortBy={tempSortBy}
                setTempSortBy={setTempSortBy}
                handleApplyFilters={handleUpdateFilter}
                handleCancelFilters={handleCancelFilter}
                searchLabel="Search by item or owner name"
                sortLabel="Sort by"
            />
        </StyledContainer>
    );
}

export default MyBookings;
